'use strict';
const angular = require('angular');
const {copy, equals, forEach, isFunction, isNumber, isObject, isString, noop} = angular;
const keys = require('lodash/keys');
const union = require('lodash/union');

import {DOMAIN_FIELD_TYPES} from './DOMAIN_FIELD_TYPES';

export const DomainValuesImport = ['$log', '$window', '$q', '$domain', '$domainCache', '$exceptionHandler', '$$DomainCollectionInstance', $$DomainValues];

export function DomainValues(instance) {
  Object.defineProperty(this, '$$parent', {enumerable: false, value: instance});
}

export function DomainValue(propertyName, constraint, instance) {
  Object.defineProperties(this, {
      name: {enumerable: true, value: propertyName},
      constraint: {value: constraint},
      value: {enumerable: true, writable: true, value: null},
      originalValue: {enumerable: true, writable: true, value: null},
      $$dirty: {enumerable: true, writable: true, value: false},
      $$parent: {enumerable: false, value: instance}
    }
  );
}

function $$DomainValues($log, $window, $q, $domain, $domainCache, $exceptionHandler, DomainCollectionInstance) {
  var JSON = $window.JSON;

  DomainValues.prototype.$defineValue = function $defineValue(propertyName, constraint) {
    Object.defineProperty(this, propertyName, {
      enumerable: true,
      value: new DomainValue(propertyName, constraint, this.$$parent)
    });
  };

  DomainValues.prototype.$hydrate = function $hydrate(serializedData) {
    var values = this;
    var instance = this.$$parent;
    if (isObject(serializedData)) {
      forEach(union(keys(serializedData), keys(instance.$constraints)), function (key) {
        var value = serializedData[key];

        // Convert supported Data Types
        if (values.hasOwnProperty(key)) {
          values[key].hydrate(value);
        } else { // non-persisted fields, such as $id and $ref()
          instance[key] = value;
        }
      });

      if (serializedData.$id && !instance.$id) {
        //console.log('$hydrate assigning ID:', instance.$id, serializedData.$id);
        instance.$id = serializedData.$id;
      }
    }
  };

  DomainValues.prototype.$loaded = function $loaded() {
    var promises = [];
    for (var field in this) {
      if (this.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
        if (this[field].constraint.type === 'Domain') {
          var value = this[field].$$dirty ? this[field].value : this[field].originalValue;
          value && promises.push(_retrieveDomainInstance(value.collection, value.id));
        } else if (this[field].constraint.type === 'NestedDocument' && this[field].originalValue) {
          promises.push(this[field].originalValue.$loaded())
        }
      }
    }

    return promises.length ? $q.all(promises) : $q.when(null);
  };

  DomainValues.prototype.$update = function $update(data) {
    var promises = [];
    var values = this;
    var instance = this.$$parent;
    data = isObject(data) && data || {};
    for (var field in values) {
      if (values.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
        if (data.hasOwnProperty(field)) {
          if (values[field].constraint.type === 'NestedDocument') {
            values[field].originalValue.$update(data[field]);
          } else {
            promises.push(values[field].hydrate(data[field]));
          }
        } else {
          values[field].originalValue = null;
        }
      }
    }

    for(var key in data) {
      if (data.hasOwnProperty(key) && !values.hasOwnProperty(key)) {
        instance[key] = data[key];
      }
    }

    return promises.length ? $q.all(promises) : $q.when(null);
  };

  DomainValues.prototype.$isDirty = function $isDirty() {
    for (var field in this) {
      if (this.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
        if (this[field].$$dirty) {
          return true;
        }
      }
    }
    return false;
  };

  DomainValues.prototype.$serialize = function $serialize(includeAll = false) {
    var data = {};
    var result;

    for (var field in this) {
      if (this.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
        //console.log('serializing ' + field + ' with value ' + this[field]);
        result = this[field].serialize(true);
        if (includeAll === true || result.include) {
          data[field] = result.value;
        }
      }
    }

    return data;
  };

  DomainValues.prototype.$write = function $write() {
    for (var field in this) {
      if (this.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
        if (this[field].constraint.type === 'NestedDocument') {
          // TODO(NestedDocument): tighten up API / behavior requirements
          this[field].originalValue.$$write();
        } else if (this[field].isDirty()) {
          this[field].$$dirty = false;

          if (this[field].value === $window.Firebase.ServerValue.TIMESTAMP) {
            this[field].originalValue = new Date(); // this needs to retrieve the date according to the server
          } else {
            this[field].originalValue = this[field].value;
          }
          this[field].value = null;
        }
      }
    }
  };

  DomainValues.prototype.$revert = function $revert(propertyName) {
    if (typeof propertyName !== 'undefined') {
      if (this.hasOwnProperty(propertyName)) {
        if (this[propertyName].constraint.type === 'NestedDocument') {
          this[propertyName].originalValue.$revert();
        } else if (this[propertyName].isDirty()) {
          this[propertyName].$$dirty = false;
          this[propertyName].value = null;
        }
      } else if (!__isEnumerable(this[propertyName].constraint)) {
        throw new Error('property "' + propertyName + '" is not revertible on class "' + this.$class.$name + '"');
      } else {
        throw new Error('property "' + propertyName + '" is not defined on class "' + this.$class.$name + '"');
      }
    } else {
      for (var field in this) {
        if (this.hasOwnProperty(field) && __isEnumerable(this[field].constraint)) {
          if (this[field].constraint.type === 'NestedDocument') {
            this[field].originalValue.$revert();
          } else if (this[field].isDirty()) {
            this[field].$$dirty = false;
            this[field].value = null;
          }
        }
      }
    }
  };

  DomainValues.prototype.$updateAccountingValues = function updateAccountingValues() {
    if (this.dateCreated && this.dateCreated.constraint.type === 'Date') {
      if (!this.dateCreated.originalValue) {
        this.dateCreated.value = $window.Firebase.ServerValue.TIMESTAMP;
        this.dateCreated.$$dirty = true;
      }
    }

    if (this.lastUpdated && this.lastUpdated.constraint.type === 'Date') {
      if (this.$isDirty()) {
        this.lastUpdated.value = $window.Firebase.ServerValue.TIMESTAMP;
        this.lastUpdated.$$dirty = true;
      }
    }
  };


  DomainValue.prototype.defineGetter = function () {
    var domainValue = this;
    var getMethodName = '$get' + domainValue.name.charAt(0).toUpperCase() + domainValue.name.slice(1);
    if (domainValue.constraint.type === 'NestedDocument') {
      return function () {
        return domainValue.originalValue
      }
    } else if (domainValue.constraint.type === 'DomainCollection') {
      return function () {
        if (!domainValue.originalValue) {
          domainValue.originalValue = new DomainCollectionInstance(domainValue.$$parent, domainValue.constraint.collectionClass);
        }
        return domainValue.originalValue;
      };
      // TODO(DomainCollection): retrieve appropriate collection list reference
    } else if (domainValue.constraint.type === 'LinkedDocument') {
      return domainValue.$$parent[getMethodName];//.bind(domainValue.$$parent)
    } else if (domainValue.constraint.type === 'Domain') {
      if (domainValue.constraint.domainClass && domainValue.constraint.simpleLink) {
        return function () {
          var value = domainValue.$$dirty ?
            domainValue.value :
            domainValue.originalValue;

          return isString(value) ?
            domainValue.getDomainInstance(domainValue.constraint.domainClass.COLLECTION, value) :
            null;
        }
      } else {
        return function () {
          var value = domainValue.$$dirty ?
            domainValue.value :
            domainValue.originalValue;

          return isObject(value) && value.id && value.collection ?
            domainValue.getDomainInstance(value.collection, value.id) :
            null;
        }
      }
    } else if (domainValue.constraint.type === 'Date') {
      return function () {
        var value = domainValue.$$dirty ?
          domainValue.value :
          domainValue.originalValue;

        return value === $window.Firebase.ServerValue.TIMESTAMP ?
          new Date() :
          value;
      }
    } else {
      return function () {
        return domainValue.$$dirty ?
          domainValue.value : /* may be changed by client. discarded if not saved */
          domainValue.originalValue;
        /* dynamically updated by firebase */
      }
    }
  };

  DomainValue.prototype.defineSetter = function () {
    var domainValue = this;
    if (domainValue.constraint.type === 'NestedDocument') {
      return function (value) {
        //TODO(NestedDocument): determine if we should throw an error if an attempt is made to overwrite the nested document?
      }
    } else if (domainValue.constraint.type === 'DomainCollection') {
      //  // TODO(DomainCollection): do nothing or throw error? probably the same handling as the NestedDocument
      return noop;
    } else if (domainValue.constraint.type === 'LinkedDocument') {
      return noop; // does not have a setter
    } else if (domainValue.constraint.type === 'Domain') {
      if (domainValue.constraint.domainClass && domainValue.constraint.simpleLink) {
        return function (value) {
          if (value && value.$id) {
            domainValue.value = value.$id;
          } else {
            domainValue.value = null;
          }
          domainValue.$$dirty = domainValue.value !== domainValue.originalValue;
        }
      } else {
        return function (value) {
          if (value && value.$id) {
            domainValue.value = {id: value.$id, collection: value.$class.COLLECTION};
            //} else if (value && value.id && value.collection) {
            //  domainValue.value = {id: value.id, collection: value.collection};
          } else {
            domainValue.value = null;
          }

          // check if field is dirty
          domainValue.$$dirty = domainValue.domainLinkIsDirty();
        }
      }
    } else if (domainValue.constraint.type === 'DomainLink') {
      if (domainValue.constraint.domainClass && domainValue.constraint.simpleLink) {
        return function (value) {
          if (isObject(value) && isString(value.$id)) {
            domainValue.value = value.$id;
          } else if (isString(value)) {
            domainValue.value = value;
          } else {
            domainValue.value = null;
          }
          domainValue.$$dirty = domainValue.value !== domainValue.originalValue;
        }
      } else {
        return function (value) {
          if (isObject(value) && isString(value.$id)) {
            domainValue.value = {id: value.$id, collection: value.$class.COLLECTION};
          } else if (isObject(value) && isString(value.id) && isString(value.collection)) {
            domainValue.value = {id: value.id, collection: value.collection};
          } else {
            domainValue.value = null;
          }
          domainValue.$$dirty = domainValue.domainLinkIsDirty();
        }
      }
    } else if (domainValue.constraint.type === 'JSON' || domainValue.constraint.type === 'GeoJSON') {//  if (this.$$constraints[field].validateType(field))
      return function (value) {
        domainValue.value = value && copy(value) || null; // may be changed by client. discarded if not saved

        // check if field is dirty
        domainValue.$$dirty = !equals(domainValue.value, domainValue.originalValue);
      }
    } else if (domainValue.constraint.type === 'Date') {
      return function (value) {
        if (isNumber(value)) {
          domainValue.value = new Date(value);
        } else {
          domainValue.value = value;
        }

        // check if field is dirty
        domainValue.$$dirty = domainValue.value !== domainValue.originalValue;
      }
    } else {
      return function (value) {
        domainValue.value = value; // may be changed by client. discarded if not saved

        // check if field is dirty
        domainValue.$$dirty = domainValue.value !== domainValue.originalValue;
      }
    }
  };

  DomainValue.prototype.domainLinkIsDirty = function () {
    var domainValue = this;
    if (domainValue.value && domainValue.originalValue) {
      return domainValue.value.id !== domainValue.originalValue.id || domainValue.value.collection !== domainValue.originalValue.collection;
    } else if (domainValue.originalValue && !domainValue.value) {
      return true;
    } else if (!domainValue.originalValue && domainValue.value) {
      return true;
    } else { // both are null
      return false;
    }
  };

  DomainValue.prototype.isDirty = function () {
    if (this.constraint.type === 'NestedDocument') {
      this.originalValue.$isDirty();
    } else {
      return this.$$dirty;
    }
  };

  DomainValue.prototype.hydrate = function (value) {
    var promise;
    var constraintType = this.constraint.type;

    if (constraintType === 'NestedDocument') {
      if (this.originalValue) {
        this.originalValue.$update(value);
      } else {
        this.originalValue = new this.constraint.nestedDocumentClass(this.$$parent, value, this.name);
      }
    } else if (constraintType === 'DomainCollection') {
      // TODO(DomainCollection): do nothing or throw error? probably the same handling as the NestedDocument
    } else if (constraintType === 'LinkedDocument') {
      // Do Nothing. This field is strictly lazy loading
    } else if (constraintType === 'Date' && isNumber(value)) {
      this.originalValue = new Date(value);
    } else if (constraintType === 'Domain') {
      var link = null;
      if (this.constraint.domainClass && this.constraint.simpleLink) {
        link = value ? {id: value, collection: this.constraint.domainClass.COLLECTION} : null;
      } else if (isObject(value)) {
        if (isString(value.collection) && isString(value.id)) {
          link = value;
        }
      } else if (isObject(value) && value.$class && isString(value.$class.COLLECTION) && isString(value.$id)) {
        link = {
          collection: value.$class.COLLECTION,
          id: value.$id
        };
      } else {
        this.originalValue = value;
      }

      if (link && link.id) {
        if (isFunction(this.constraint.domainClass)) {
          this.originalValue = this.constraint.simpleLink ? link.id : link;
          promise = this.constraint.domainClass.$get(link.id);
        } else if (link.collection) {
          var domainClass = $domain.getDomain(link.collection);
          this.originalValue = this.constraint.simpleLink ? link.id : link;
          promise = domainClass.$get(link.id);
        }
      } else {
        this.originalValue = value;
        if (link)
          $log.warn('Type conversion failed. Provided invalid domain class ', value);
      }
    } else if (isString(value) && (constraintType === 'GeoJSON' || (constraintType === 'JSON'))) {
      if (this.constraint.stringify || constraintType === 'GeoJSON') {
        this.originalValue = __tryCatchValue(JSON.parse.bind(JSON, value));
      } else if (isFunction(this.constraint.hydrator)) {
        this.originalValue = __tryCatchValue(this.constraint.hydrator.bind({}, value)); // may need to address context
      } else {
        this.originalValue = value;
      }
    } else {
      this.originalValue = value;
    }
    return promise || $q.when(null);
  };

  //TODO: define get domain object method
  DomainValue.prototype.getDomainInstance = function (collection, id) {
    if (collection && id) {
      var cached = $domainCache.get(collection, id);
      if (cached) {
        return cached;
      } else {
        var domain = $domain.getDomain(collection);
        var promise = domain.$get(id);

        // TODO: add promise to current loading list

        return null;
      }
    } else {
      return null;
    }
  };

  DomainValue.prototype.serialize = function (dirtyOnly) {
    var include = false;
    var result;

    if (this.constraint.type === 'NestedDocument') {
      return this.originalValue ?
        this.originalValue.$serialize() :
        null;
    }

    if (this.isDirty()) {
      include = true;
      result = this.value;
    } else {
      result = this.originalValue;
    }

    if (result && this.constraint.type === 'Date' && result !== $window.Firebase.ServerValue.TIMESTAMP) {
      result = result.getTime();
    }

    if (result && this.constraint.type === 'JSON' && this.constraint.stringify) {
      if (this.constraint.stringify) {
        result = __tryCatchValue(JSON.stringify.bind(JSON, result));
      } else if (isFunction(this.constraint.serializer)) {
        result = __tryCatchValue(this.constraint.serializer.bind({}, result)); // may need to address context
      } // do not change result otherwise.
    }

    if (result && this.constraint.type === 'GeoJSON') {
      try {
        result = JSON.stringify(result);
      } catch (e) {
        $exceptionHandler(e);
      }
    }

    if (dirtyOnly) {
      return {
        include: include,
        value: result
      };
    } else {
      return result;
    }
  };

  DomainValue.prototype.toString = function () {
    var string = [
      'name: ' + this.name,
      'isDirty: ' + this.$$dirty,
      'value: ' + this.value,
      'originalValue: ' + this.originalValue
    ];

    return "[" + string.join(', ') + "]"
  };

  DomainValue.prototype.isEnumerable = function isEnumerable() {
    return __isEnumerable(this.constraint);
  };

  function __isEnumerable(constraint) {
    return DOMAIN_FIELD_TYPES[constraint.type] && DOMAIN_FIELD_TYPES[constraint.type].enumerable;
  }

  function _retrieveDomainInstance(collection, id) {
    if (!collection || !id) {
      return $q.when(null);
    } else {
      var cached = $domainCache.get(collection, id);
      return cached ?
        $q.when(cached) :
        $domain.getDomain(collection).$get(id).catch(function (e) {
          $exceptionHandler("Failed to retrieve collection:" + collection + ", id:" + id, e);
          return $q.reject(e);
        });
    }
  }

  function __tryCatchValue(fn) {
    try {
      return fn();
    } catch (e) {
      $exceptionHandler('DomainValues: an error occurred', e);
      return null;
    }
  }

  return {
    DomainValue,
    DomainValues,
  };
}



// WEBPACK FOOTER //
// ./src/js/_libs/angular-domain/domain-properties/DomainValues.js